import { makeStyles, Theme } from '@material-ui/core'
import classnames from 'classnames'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import behead from 'remark-behead'
import directive from 'remark-directive'
import footnotes from 'remark-footnotes'
import gfm from 'remark-gfm'
import slug from 'remark-slug'
import toc from 'remark-toc'
import { PluggableList } from 'unified'

import footnotesTitle from './rehype-plugins/footnotes-title'
import htmlDirectives from './remark-plugins/html-directives'
import renderers from './renderers'

const REMARK_PLUGINS: PluggableList = [
  gfm,
  [behead, { depth: 1 }],
  slug,
  [footnotes, { inlineNotes: true }],
  directive,
  htmlDirectives,
]

const REHYPE_PLUGINS: PluggableList = [footnotesTitle]

const useStyles = makeStyles<Theme, { preserveTopMargin: boolean }>(
  (theme) => ({
    root: {
      '&>:first-child': {
        marginTop: ({ preserveTopMargin }: { preserveTopMargin: boolean }) =>
          preserveTopMargin ? 'default' : 0,
      },
      '& ol ol': {
        listStyleType: 'lower-alpha',
      },
      '& ol ol ol': {
        listStyleType: 'lower-roman',
      },
      '& .footnotes': {
        '& ol': {
          paddingLeft: '1em',
        },
        '& li': {
          fontSize: '0.875em',
          marginBottom: theme.spacing(2),
        },
      },
    },
  }),
)

type MarkdownProps = {
  children: string
  TOC?: string
  className?: string
  preserveTopMargin?: boolean
}

const Markdown: React.FC<MarkdownProps> = ({
  children,
  TOC,
  className,
  preserveTopMargin = false,
}) => {
  const classes = useStyles({ preserveTopMargin })
  const plugins = [...REMARK_PLUGINS]

  // Table of Contents logic
  let showTOC = false
  let showIntroduction = false
  let headingLeader = ''
  if (TOC) {
    // get the depth of the top level heading in the document
    const topHeadingDepth = children
      .match(/^#{1,6}/gm)
      ?.reduce((prev, curr) => {
        if (curr.length < prev) return curr.length
        return prev
      }, 6)
    if (topHeadingDepth) {
      showTOC = true
      headingLeader = [...Array(topHeadingDepth)].map(() => '#').join('')
      plugins.push([toc, { heading: TOC, maxDepth: 5, tight: true }])
      /**
       * Figure out if we need to insert an '# Introduction' line between the
       * TOC and the rest of the content – the TOC plugin removes everything
       * that's between the TOC heading and the next heading of equivalent or
       * higher depth. Because a number of legacy documents don't always have a
       * heading as their first line of text, this could lead to unwanted
       * deletions.
       *     To remedy this, we insert an 'Introduction' heading, at the same depth
       * as the TOC + first 'real' heading. This means that the Introduction
       * section gets added to the TOC and the content isn't deleted. See this
       * issue for someone else complaining about this:
       * https://github.com/remarkjs/remark-toc/issues/18
       */
      const firstHeading = children.match(/#{1,6}/)
      if (firstHeading?.index && firstHeading?.index > 0)
        showIntroduction = true
    }
  }

  const input = `${
    TOC && showTOC
      ? `${headingLeader} ${TOC}\n\n${
          showIntroduction ? `${headingLeader} Introduction\n\n` : ''
        }`
      : ''
  }${children}`

  return (
    <div className={classnames(classes.root, className)}>
      <ReactMarkdown
        components={renderers}
        remarkPlugins={plugins}
        rehypePlugins={REHYPE_PLUGINS}
      >
        {input}
      </ReactMarkdown>
    </div>
  )
}

export default Markdown
